import { SelectControl } from "../common";
import { FormattedMessage, useIntl } from "react-intl";
import {
  edsn_Marktrol,
  MprService,
  RegistrationCreateDto,
} from "../../openapi/edsn-webapi";
import { Alert, Link, SelectChangeEvent } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { Language, useLocalizationStore } from "../Localization";
import { useQueryWrapper } from "../../hooks";
import { useEffect, useMemo } from "react";

type MarketroleSelectControlProps = {
  label?: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  isAdd: boolean;
  onChange?: (
    onChange: (...event: any[]) => void
  ) => (e: SelectChangeEvent<any>, child: React.ReactNode) => void;
};

const MarketroleSelectControl = ({
  label = "marketRole",
  name,
  required,
  disabled,
  isAdd,
  onChange,
}: MarketroleSelectControlProps) => {
  const intl = useIntl();
  const { setError, clearErrors, watch } = useFormContext<RegistrationCreateDto>();
  const marketRole = watch("marketRole");
  const isOda = marketRole === edsn_Marktrol.ODAOVERIGE_DIENSTENAANBIEDER;
  const language = useLocalizationStore((state) => state.language);
  const url = language === Language.DUTCH ? "faq-oda" : "faq-oda-en";

  const { data, isInitialLoading } = useQueryWrapper(
    ["getMprOrganisationInformation"],
    () => MprService.getMprOrganisationInformation(),
    { staleTime: Infinity, gcTime: Infinity, enabled: !isAdd },
  );

  const options = useMemo(() => {
    const allRoles = Object.values(edsn_Marktrol).filter(
      (value) =>
        value !== edsn_Marktrol.RNBREGIONALE_NETBEHEERDER &&
        value !== edsn_Marktrol.LNBLANDELIJK_NETBEHEERDER &&
        value !== edsn_Marktrol.ANDERSNAMELIJK
    );

    const filteredRoles = isAdd
      ? allRoles
      : allRoles.filter((role) =>
          data?.marketParties?.some((party) => party.marketRole === role)
        );

    return filteredRoles.map((value) => ({
      value,
      label: intl.formatMessage({ id: `edsn_Marktrol.${value}` }),
    }));
  }, [isAdd, data, intl]);

  useEffect(() => {
    if (!options.length && !isInitialLoading) {
      setError(name as keyof RegistrationCreateDto, {
        type: "manual",
        message: intl.formatMessage({ id: "noRegistrationsFound" }),
      });
    } else {
      clearErrors(name as keyof RegistrationCreateDto);
    }
  }, [options, isInitialLoading, setError, clearErrors, name, intl]);

  return (
    <>
      <SelectControl
        name={name}
        label={label}
        options={options}
        required={required}
        disabled={disabled}
        isLoading={isInitialLoading}
        onChange={onChange}
      />
      {isOda && (
        <Alert severity="info" sx={{ mb: 1 }}>
          <FormattedMessage
            id="odaFaqInformation"
            values={{
              link: (
                <Link
                  variant="subtitle2"
                  underline="hover"
                  href={`${process.env.REACT_APP_MIJN_PIE_BASE_URL}/${url}`}
                  target="_blank"
                >
                  <FormattedMessage id="odaFaqLinkText" />
                </Link>
              ),
            }}
          />
        </Alert>
      )}
    </>
  );
};

export default MarketroleSelectControl;
